import { motion } from 'framer-motion';
import { AlertCircle, Users, Flame, UserX } from 'lucide-react';
import { SplitWords, FadeSlideIn } from './AnimatedText';
import { TEST_IDS } from '@/constants/testIds';

const PAIN_POINTS = [
  { icon: Users, text: 'Your team is in constant conflict — and you\'re caught in the middle' },
  { icon: Flame, text: 'Founder burnout is eroding your vision and your energy' },
  { icon: UserX, text: 'A key creative has become "difficult" — and it\'s costing you' },
  { icon: AlertCircle, text: 'Post-acquisition culture clash is tearing things apart' },
];

export default function AgitationSection() {
  return (
    <section
      id="agitation"
      data-testid={TEST_IDS.agitationSection}
      className="bg-[#1B2A4A] py-24 md:py-32 px-6 md:px-12 relative overflow-hidden"
    >
      <div className="dot-pattern absolute inset-0" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
            <motion.span
              initial={{ width: 0 }}
              whileInView={{ width: 30 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-block h-px bg-[#C9A96E] mr-3 align-middle"
            />
            Does This Sound Familiar?
          </p>
          <h2
            data-testid={TEST_IDS.agitationHeadline}
            className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-[#F7F5F2] mb-12"
          >
            <SplitWords delay={0.1}>Your Team Is in Crisis. And You're Out of Ideas.</SplitWords>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {PAIN_POINTS.map((point, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 25, x: i % 2 === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, y: 0, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ x: 6, borderColor: 'rgba(201,169,110,0.3)' }}
              className="flex items-start gap-4 p-6 border border-[#F7F5F2]/8 transition-all duration-300 group"
            >
              <motion.div
                whileHover={{ rotate: 10, scale: 1.1 }}
                className="shrink-0"
              >
                <point.icon className="w-5 h-5 text-[#C9A96E] mt-0.5 group-hover:text-[#D4853A] transition-colors duration-300" />
              </motion.div>
              <p className="text-[#F7F5F2]/80 font-sans text-base leading-relaxed group-hover:text-[#F7F5F2] transition-colors duration-300">
                {point.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Mini testimonial */}
        <FadeSlideIn delay={0.4}>
          <div className="border-l-2 border-[#C9A96E] pl-8 max-w-2xl">
            <p className="font-serif text-xl md:text-2xl text-[#F7F5F2]/90 italic leading-relaxed mb-4">
              "Sarah helped me get through the toughest year of my professional life. I emerged renewed and energized."
            </p>
            <p className="text-xs uppercase tracking-[0.15em] text-[#C9A96E] font-sans">
              Ian Pierpont — Founder & CEO, Further & Further Strategies
            </p>
          </div>
        </FadeSlideIn>
      </div>
    </section>
  );
}
